// ============================================================================
// JARVISTRAVEL - TRIPS API
// Version: 1.0.0
// Trip data API calls — list, detail (composite), create, update, delete
// ============================================================================

import { api } from "./client";
import { isTransientStatus, reportApiFailure } from "./failure";
import { asyncDelay } from "@/app/utils";
import type { ActualExpenseSource } from "@/app/types";

// ---------------------------------------------------------------------------
// Raw backend response types (snake_case)
// ---------------------------------------------------------------------------

export interface TripListItem {
  id: string;
  name: string;
  destination: string;
  country?: string;
  image_url?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  adults_count: number;
  children_count: number;
  traveler_names?: string[];
  /** The caller's access: "owner", "edit" or "view". */
  access?: string;
  created_at: string;
  updated_at: string;
}

export interface ListTripsResponse {
  trips: TripListItem[];
}

export interface BudgetCategorySummary {
  id: string;
  name: string;
  allocated: number;
  spent: number;
  color?: string;
  icon?: string;
}

export interface TransactionSummary {
  id: string;
  description: string;
  amount: number;
  category: string;
  date: string;
  notes?: string;
  payment_method?: string;
  /** The currency core recorded the expense in. */
  currency?: string;
  /** Set on an actual expense only: where core got it. */
  source?: ActualExpenseSource;
}

export interface BudgetSummary {
  total: number;
  spent: number;
  remaining: number;
  currency: string;
  per_person: number;
  categories: BudgetCategorySummary[];
  transactions: TransactionSummary[];
}

export interface ComponentSummary {
  score: number;
  weight: number;
  description: string;
}

export interface FatigueSummary {
  score: number;
  level: string;
  breakdown?: Record<string, ComponentSummary>;
  recommendations?: string[];
  factor_contributions?: FactorContribution[];
}

export interface FactorContribution {
  name: string;
  label: string;
  score: number;
  weight: number;
  weighted: number;
  description: string;
}

export interface ActivitySummary {
  id: string;
  name: string;
  /** Coarse item kind from the backend (`item_type`): "activity" | "flight" | "transport" | … */
  type: string;
  /** Fine activity kind (`activity_type`): "restaurant" | "attraction" | "tour" | …
   *  Present only on activity items; drives the FE type/icon (JAR-449). */
  activity_type?: string;
  time: string;
  duration: number;
  location?: string;
  /** The stop's coordinates, once core has geocoded it (JAR-2343 / JAR-2332).
   *  Omitted until then — absent or null means no coordinates, never 0. Peter's
   *  core PR for JAR-2332 carries the exact names; if they differ, that PR wins. */
  latitude?: number;
  longitude?: number;
  cost?: number;
  /** The currency `cost` is in. Core stores what the create sends and, since
   *  core#1057 (JAR-2411, deployed 2026-10-02), its trip detail answers with it;
   *  `undefined` only for a legacy row saved before then (JAR-2416). */
  currency?: string;
  booked: boolean;
  fatigue_impact: number;
  notes?: string;
  locked?: boolean;
  lock_reason?: string;
  lock_type?: string;
  /** Who's going, as trip members' user ids (JAR-2014). Core always sends it on
   *  read; empty means everyone. */
  going?: string[];
  /** Where the stop links, and what that page is (JAR-2450). Core finds it after
   *  the stop is saved, so it's absent until then, and when there's none. */
  link_url?: string;
  link_kind?: "website" | "booking" | "provider";
  /** Which flight a flight stop is. Core answers these since core#1131
   *  (JAR-2555), on a stop it holds as a confirmed flight, and omits each one
   *  it has no value for. */
  flight_number?: string;
  airline?: string;
  departure_airport?: string;
  arrival_airport?: string;
}

export interface DaySummary {
  date: string;
  day_number: number;
  location?: string;
  activities: ActivitySummary[];
  /** The inbound flight's time-zone change on this day, worked out by core
   *  from the trip's stored flights (JAR-1763). Absent when there is none. */
  tz_shift_hours?: number;
  /** Calendar days since that flight landed; absent on the day it lands. */
  days_since_shift?: number;
}

export interface CollaboratorSummary {
  id: string;
  email: string;
  permission: string;
  status: string;
}

export interface TripStatsSummary {
  restaurants: number;
  attractions: number;
  flights: number;
  photos: number;
}

export interface TripDetailResponse {
  id: string;
  name: string;
  destination: string;
  country?: string;
  image_url?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  adults_count: number;
  children_count: number;
  traveler_names?: string[];
  planner_notes?: string;
  /** How many more times this trip's dates can change, null when no limit
   *  applies. Always sent, so null reads as "no limit", not as "not sent"
   *  (JAR-1819). */
  date_changes_left?: number | null;
  /** When the trip's planning closes, and its expenses after it, by the rule
   *  core's planning lock enforces. Null when the trip never closes, or when
   *  core could not read it (JAR-1865). */
  planning_closes_at?: string | null;
  expenses_close_at?: string | null;
  /** The caller's access, as on a list item. Absent when core couldn't read it. */
  access?: string;
  created_at: string;
  updated_at: string;

  budget?: BudgetSummary;
  fatigue_index?: FatigueSummary;
  itinerary?: DaySummary[];
  collaborators?: CollaboratorSummary[];
  stats?: TripStatsSummary;
  weather?: WeatherSummary;
}

export interface ForecastDaySummary {
  day: string;
  high: number;
  low: number;
  icon: string;
}

export interface WeatherSummary {
  temperature: number;
  feels_like: number;
  condition: string;
  humidity: number;
  wind_speed: number;
  icon: string;
  forecast?: ForecastDaySummary[];
}

// ---------------------------------------------------------------------------
// CRUD types
// ---------------------------------------------------------------------------

export interface CreateTripRequest {
  name: string;
  destination: string;
  country?: string;
  image_url?: string;
  start_date?: string;
  end_date?: string;
  adults_count?: number;
  children_count?: number;
  party_type?: string;
  child_age_bands?: string[];
  accessibility_needs?: string;
  traveler_names?: string[];
  planner_notes?: string;
}

export interface UpdateTripRequest {
  name?: string;
  destination?: string;
  country?: string;
  image_url?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  adults_count?: number;
  children_count?: number;
  traveler_names?: string[];
  planner_notes?: string;
}

export interface TripResponse {
  id: string;
  name: string;
  destination: string;
  // What core answers an update with, as the edit sheet reads it back
  // (JAR-1853). Core sends more; this says only what has a reader.
  start_date?: string;
  end_date?: string;
  adults_count?: number;
  children_count?: number;
  /** As on the trip read: null when no limit applies (JAR-1819). */
  date_changes_left?: number | null;
  planner_notes?: string;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetch all trips accessible to the current user (owned + collaborated).
 *
 * @throws Error with backend message on failure
 */
export async function listTrips(): Promise<TripListItem[]> {
  const result = await api.get<ListTripsResponse>("/trips");

  if (result.error) {
    throw new Error(result.error.message || "Failed to list trips");
  }

  return result.data?.trips || [];
}

/**
 * Get the composite trip detail (trip + budget + fatigue + itinerary + collaborators).
 * Returns null if the trip is not found or the user lacks access.
 */
export async function getTripDetail(tripId: string): Promise<TripDetailResponse | null> {
  const result = await api.get<TripDetailResponse>(`/trips/${tripId}/detail`);

  if (result.error) {
    return null;
  }

  return result.data;
}

/** What a trip read found, or why it found nothing. */
export type TripRead =
  | { ok: true; detail: TripDetailResponse }
  /** `status` is the answer's HTTP status, for a caller that must tell a trip
   *  that is gone from an answer that says nothing about it. Without one, the
   *  trip can't be taken as gone. */
  | { ok: false; failure: "not-found" | "unreachable"; status?: number };

/**
 * The same read as {@link getTripDetail}, except it says WHY it failed.
 *
 * `getTripDetail` answers `null` for a missing trip and for an unreachable backend
 * alike, so its caller cannot tell them apart. `useTripById` filled that gap by
 * reading the process-wide reachability store when the promise settled, and another
 * request can flip that store in between — so a trip that failed during an outage
 * rendered as "That trip isn't in your journal", which is the regression JAR-1434
 * fixed and JAR-1461 reopened.
 *
 * What counts as unreachable: a network error (no answer at all), a 5xx (the
 * backend could not serve us — `client.ts` marks it `serverFailing` at its 5xx
 * limb), or an answered 408/429 (`isTransientStatus`). The 429 is the subtle one:
 * core answered, and the client even marks the backend reachable on it, but it
 * means "rate-limited, wait a moment", not "no such trip" — so it reads as
 * transient, not a missing trip (JAR-2419). Everything else is the backend
 * saying no.
 *
 * A rate limit or timeout retries once here, honouring `Retry-After`, so the read
 * recovers on its own rather than relabelling and leaving the person to press Try
 * again. Only an answered transient retries; a 5xx/network outage rides the
 * reachability recovery, not a tight loop. Acting on `Retry-After` client-wide is
 * its own ticket (JAR-3321).
 *
 * `getTripDetail` keeps its contract: `useDashboardData` and `ShareModal` still call
 * it, and moving them is a separate change.
 */
// Scoped retry budget for a transient trip read (JAR-2419): one retry, capped so a
// large or hostile Retry-After cannot stall the read, with a short default when the
// server sent none.
const TRANSIENT_RETRY_DEFAULT_MS = 1000;
const TRANSIENT_RETRY_CAP_MS = 5000;

export async function readTripDetail(tripId: string): Promise<TripRead> {
  for (let attempt = 0; ; attempt++) {
    const result = await api.get<TripDetailResponse>(`/trips/${tripId}/detail`);

    if (result.error) {
      const unreachable =
        result.error.isNetworkError || result.status >= 500 || isTransientStatus(result.status);
      // One capped retry for an answered rate limit or timeout, honouring the
      // server's Retry-After when it sent one (JAR-2419). A 5xx or network outage
      // is not retried here — it rides the reachability recovery.
      if (attempt === 0 && isTransientStatus(result.status)) {
        await asyncDelay(Math.min(result.error.retryAfterMs ?? TRANSIENT_RETRY_DEFAULT_MS, TRANSIENT_RETRY_CAP_MS));
        continue;
      }
      return { ok: false, failure: unreachable ? "unreachable" : "not-found", status: result.status };
    }

    // An answer with no body is not a trip. Not-found rather than unreachable: the
    // backend did answer, so a "try again" would be telling the user something false.
    if (!result.data) {
      return { ok: false, failure: "not-found", status: result.status };
    }

    return { ok: true, detail: result.data };
  }
}

/**
 * Create a new trip. Core spends a Trip Pass for it unless the owner has an
 * Explore subscription, and refuses with 402 `SUBSCRIPTION_REQUIRED` when there
 * is neither.
 *
 * `idempotencyKey` makes a retry safe (JAR-1762): core answers a key it has
 * seen with the trip it made then, so a create whose answer was lost, sent
 * again, makes one trip and spends one pass. Send the same key for a retry of
 * the same trip, and a new one for a new trip. Printable ASCII, at most 255.
 *
 * @throws ApiRequestError on failure, so the caller can read `detailCode`
 */
export async function createTrip(req: CreateTripRequest, idempotencyKey?: string): Promise<TripResponse> {
  const result = await api.post<TripResponse>("/trips", req, true, idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined);

  if (result.error) {
    // A 402 is the paywall working, not a failure.
    if (result.status !== 402) reportApiFailure("[trips] create failed:", result.error);
    throw result.error;
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

export async function updateTrip(tripId: string, req: UpdateTripRequest): Promise<TripResponse> {
  const result = await api.put<TripResponse>(`/trips/${tripId}`, req);

  if (result.error) {
    // The error itself, so its code and status survive. A Trip Pass trip that
    // has spent its two date changes is refused with 409
    // DATE_CHANGE_LIMIT_REACHED, and one whose planning has closed with 423:
    // the edit screen tells those from any other failure (JAR-1853).
    throw result.error;
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

export async function deleteTrip(tripId: string): Promise<void> {
  const result = await api.del<{ success: boolean }>(`/trips/${encodeURIComponent(tripId)}`);

  // A 404 is a trip already gone, deleted from another tab or device: that IS
  // the delete, as a journal delete reads it (JAR-2176), not a failure every
  // retry would repeat (JAR-2602 review).
  if (result.error && result.status !== 404) {
    // The error itself, so its status survives: a refusal is not a dropped
    // connection, and the screen says which.
    reportApiFailure("[trips] trip delete failed:", result.error);
    throw result.error;
  }
}

/**
 * Delete one itinerary item.
 * DELETE /v1/trips/{trip_id}/itinerary-items/{item_id}
 *
 * @throws ApiRequestError on failure
 */
export async function deleteItineraryItem(tripId: string, itemId: string): Promise<void> {
  const result = await api.del<{ success: boolean }>(
    `/trips/${encodeURIComponent(tripId)}/itinerary-items/${encodeURIComponent(itemId)}`
  );
  if (result.error) {
    reportApiFailure("[trips] itinerary item delete failed:", result.error);
    throw result.error;
  }
}

/** One itinerary item as core creates it. Times are "HH:MM", dates "YYYY-MM-DD". */
export interface CreateItineraryItemRequest {
  item_type: "flight" | "activity" | "transport";
  name: string;
  date: string;
  start_time: string;
  duration_minutes: number;
  location?: string;
  price?: number;
  currency?: string;
  booked: boolean;
  /** 1 to 10. */
  fatigue_impact: number;
  order_index: number;
  /** Required by core on a booked flight. */
  flight_number?: string;
  airline?: string;
  departure_airport?: string;
  arrival_airport?: string;
  /** Required by core on an activity item. */
  activity_type?: string;
  description?: string;
  /** Who's going, as trip members' user ids (JAR-2014). Omit or [] means
   *  everyone. */
  going?: string[];
  /** The Explore provider's page a stop is added from. Core keeps it only on an
   *  allowlisted site, and finds every other stop's link itself (JAR-2450). */
  link_url?: string;
  /** Where the stop is, when it's known: core places a stop sent with both at
   *  once, instead of looking it up after it's saved (JAR-3141). */
  latitude?: number;
  longitude?: number;
}

/** Core's limits on one bulk call: at most 500 items, in a body of at most
 *  1 MiB (bulkCreateItineraryItems in core's docs/openapi.yaml). The byte
 *  budget leaves room for the envelope around the items. */
export const MAX_BULK_ITINERARY_ITEMS = 500;
export const MAX_BULK_ITINERARY_BYTES = 1_000_000;

/** A created item, as core answers the bulk call. Only the id is read here. */
export interface CreatedItineraryItem {
  id: string;
}

/**
 * Create many itinerary items in one transaction: all of them, or none.
 * POST /v1/trips/{trip_id}/itinerary-items/bulk
 *
 * `idempotencyKey` makes a retry safe: core answers a key this trip has already
 * written with the items that write made, and writes nothing (JAR-1821).
 *
 * @returns the created items, in the order they were sent
 * @throws ApiRequestError on failure
 */
export async function createItineraryItems(
  tripId: string,
  items: CreateItineraryItemRequest[],
  idempotencyKey?: string
): Promise<CreatedItineraryItem[]> {
  const result = await api.post<{ items: CreatedItineraryItem[] }>(
    `/trips/${encodeURIComponent(tripId)}/itinerary-items/bulk`,
    { items },
    true,
    idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined
  );

  if (result.error) {
    reportApiFailure("[trips] bulk itinerary create failed:", result.error);
    throw result.error;
  }

  // A success means core saved the call, even if its answer lists no items:
  // throwing here would report saved stops as unsaved and invite adding them
  // twice. Stops it gave no id for keep their own until the trip is read back.
  return result.data?.items ?? [];
}

/** One item's part in a plan edit: its id, and only the fields that changed. */
export interface PlanItemUpdate {
  id: string;
  name?: string;
  date?: string;
  start_time?: string;
  duration_minutes?: number;
  location?: string;
  price?: number;
  currency?: string;
  activity_type?: string;
  description?: string;
  order_index?: number;
  /** Who's going, as trip members' user ids (JAR-2014). Present replaces the
   *  list; [] means everyone again. Sent only when it changed. */
  going?: string[];
}

/** One edit to a trip's plan: the items it changed, and the ones it removed. */
export interface PlanEdit {
  updates: PlanItemUpdate[];
  deletes: string[];
}

/**
 * Apply one edit to a trip's plan in one transaction: every change, or none.
 * POST /v1/trips/{trip_id}/itinerary-items/batch
 *
 * @throws ApiRequestError on failure
 */
export async function applyPlanEdit(tripId: string, edit: PlanEdit): Promise<void> {
  const result = await api.post<{ updated: number; deleted: number }>(
    `/trips/${encodeURIComponent(tripId)}/itinerary-items/batch`,
    edit
  );
  if (result.error) {
    reportApiFailure("[trips] plan edit failed:", result.error);
    throw result.error;
  }
}

/**
 * Lock an itinerary item, preventing modifications.
 * POST /v1/trips/{trip_id}/itinerary-items/{item_id}/lock
 */
export async function lockItem(
  tripId: string,
  itemId: string,
  reason?: string,
  lockType?: string
): Promise<void> {
  const body: Record<string, string> = {};
  if (reason) body.reason = reason;
  if (lockType) body.lock_type = lockType;
  await api.post<ActivitySummary>(
    `/trips/${tripId}/itinerary-items/${itemId}/lock`,
    Object.keys(body).length > 0 ? body : undefined
  );
}

/**
 * Unlock an itinerary item, allowing modifications.
 * POST /v1/trips/{trip_id}/itinerary-items/{item_id}/unlock
 */
export async function unlockItem(
  tripId: string,
  itemId: string
): Promise<void> {
  await api.post<ActivitySummary>(
    `/trips/${tripId}/itinerary-items/${itemId}/unlock`
  );
}
